import { synchronizeStoryReferences } from 'utils/blocks/story-element-reference';
import produce from 'immer';
import type { Action } from 'redux';
import type { CardEditorType } from 'types/story';
import { StoryFacade } from 'utils/facades/story-facade';
import { AdminReducerState, AdminThunkAction } from 'admin/reducers';
import { CARD_EDITOR_SYNC } from 'admin/constants/actions';
import { storyEditorChange, storyEditorFormChange } from 'admin/actions/story/update-editable-story';
import { updateLatestStory } from 'admin/actions/story/update-latest-story';

export const getMergeOfEditableStoryWithCardEditor = (
	editableStory: Exclude<AdminReducerState['storyEditor']['story'], null>,
	cardEditor: Pick<CardEditorType, 'data' | 'storySettings' | 'storyElements' | 'symbols'>,
	version: string
) => {
	return produce(editableStory, draft => {
		if (!draft.storyVersions[version]) {
			console.error('No such version in story');
			return;
		}

		const { data: cardData, storySettings, storyElements, symbols } = cardEditor;
		if (!cardData) return;

		const cardInfo = StoryFacade.findCardInSteps(cardData?._id ?? '', draft.storyVersions[version].data.steps);
		const cardIndex = cardInfo.card?.index ?? -1;
		const stepIndex = cardInfo.step?.index ?? -1;
		const cards = draft.storyVersions[version].data.steps[stepIndex]?.cards;

		if (cardData && cards[cardIndex]?._id === cardData._id) {
			cards[cardIndex] = cardData;
		}

		if (storySettings) draft.storyVersions[version].settings = storySettings;
		if (storyElements) draft.storyVersions[version].data.elements = storyElements;
		if (symbols) draft.storyVersions[version].data.symbols = symbols;

		synchronizeStoryReferences(draft.storyVersions[version].data);
	});
};

export function cardEditorSync(
	props: { updateStoryEditor?: boolean; skipLatestStoryUpdate?: boolean } = {}
): AdminThunkAction<Action, void> {
	return async (dispatch, getState) => {
		dispatch({ type: CARD_EDITOR_SYNC.SYNC });

		let error;

		try {
			const state = getState();
			const version = state.version.current;

			if (!state.storyEditor.story) {
				throw new Error(`"storyEditor.story" is null`);
			}

			const nextStory = getMergeOfEditableStoryWithCardEditor(
				state.storyEditor.story,
				state.cardEditor.present,
				version
			);
			const Story = new StoryFacade(nextStory, version);

			if (!props.skipLatestStoryUpdate) {
				dispatch(updateLatestStory({ ...Story.requestData, getStoryOnComplete: false }));
			}

			if (props.updateStoryEditor) {
				const path = `storyVersions.${version}`;
				const data = Story.story;
				await Promise.all([
					// update form.storyEditor
					dispatch(storyEditorFormChange({ path, data })),
					// update storyEditor
					dispatch(storyEditorChange({ path, data })),
				]);
			}
		} catch (err) {
			error = err;
			dispatch({ type: CARD_EDITOR_SYNC.FAIL, payload: err });
			console.error(err);
		}

		if (!error) {
			dispatch({ type: CARD_EDITOR_SYNC.SUCCESS });
		}
	};
}
